import { useEffect, useRef, useState } from "react";
import { Upload, ShieldCheck, FileText, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ConfirmResetDialog } from "@/components/ConfirmResetDialog";

// ============================================================================
// SpecializedVault — reusable Premium Shield vault portal. Parameterized by
// kind (auto|home|capital), color, icon, and label. All three vaults share
// the same halo breathe + laser sweep animation, differing only in accent
// color. Capital variant additionally renders a 5-year MACRS depreciation
// SVG curve when the response carries `depreciation_schedule`.
// ============================================================================
const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Multi-year slider — years the packet + slicer can pivot across.
const TAX_YEARS = [2024, 2025, 2026];
const TAX_YEAR_STORAGE_KEY = "rs.vault.taxYear";
const readTaxYear = () => {
  try {
    const v = Number(localStorage.getItem(TAX_YEAR_STORAGE_KEY));
    return TAX_YEARS.includes(v) ? v : new Date().getFullYear();
  } catch { return new Date().getFullYear(); }
};
const writeTaxYear = (y) => {
  try { localStorage.setItem(TAX_YEAR_STORAGE_KEY, String(y)); } catch { /* noop */ }
};
const rowMatchesYear = (r, y) => {
  const s = String(r?.date || r?.uploaded_at || "");
  return s.startsWith(`${y}-`) || s.startsWith(`${y}/`);
};

// Shared 12-week velocity sparkline. Buckets `rows` into 7-day windows counted
// back from today. Renders as a compact SVG with color-matched gradient fill.
function Sparkline({ rows, color, testid }) {
  const now = new Date();
  const weekly = new Array(12).fill(0);
  rows.forEach((r) => {
    const raw = r?.date || r?.uploaded_at;
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return;
    const daysAgo = Math.floor((now - d) / 86400000);
    const wk = Math.floor(daysAgo / 7);
    if (wk >= 0 && wk < 12) {
      weekly[11 - wk] += Number(r?.total || r?.purchase_price || 0);
    }
  });
  const wMax = Math.max(1, ...weekly);
  const W = 132, H = 30, pad = 2;
  const linePath = weekly.map((v, i) => {
    const x = pad + (i / 11) * (W - pad * 2);
    const y = H - pad - (v / wMax) * (H - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const areaPath = linePath + ` L${(W - pad).toFixed(1)},${(H - pad).toFixed(1)} L${pad.toFixed(1)},${(H - pad).toFixed(1)} Z`;
  const gradId = `spark-${testid || color.replace("#", "")}-fill`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="shrink-0" data-testid={testid} aria-label="12-week transaction velocity">
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.55" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <text x={W - 2} y={9} textAnchor="end" fontSize="7" fill="#94a3b8" fontFamily="ui-monospace, monospace">
        12wk · velocity
      </text>
    </svg>
  );
}

// YoY Delta HUD — 3 proportional bars comparing 2024 / 2025 / 2026 spend for
// the given row set. Mirrors sparkline color so each vault reads at a glance.
function YoYBars({ rows, color, testid, valueKey = "total" }) {
  const totals = TAX_YEARS.map((y) =>
    rows.filter((r) => rowMatchesYear(r, y))
        .reduce((s, r) => s + Number(r?.[valueKey] || 0), 0)
  );
  const max = Math.max(1, ...totals);
  const barMax = 22;
  return (
    <div className="flex items-end gap-1 shrink-0" data-testid={testid} aria-label="year-over-year delta bars">
      {TAX_YEARS.map((y, i) => {
        const t = totals[i];
        const h = Math.max(2, Math.round((t / max) * barMax));
        const isMax = t > 0 && t === max;
        return (
          <div key={y} className="flex flex-col items-center gap-0.5">
            <div
              className="w-4 rounded-sm"
              style={{
                height: `${h}px`,
                background: `linear-gradient(180deg, ${color}, ${color}55)`,
                boxShadow: isMax ? `0 0 8px ${color}` : undefined,
                opacity: t > 0 ? 1 : 0.35,
              }}
              data-testid={`${testid}-bar-${y}`}
              title={`TY ${y} · ${fmt(t)}`}
            />
            <span className="text-[7px] font-mono tracking-widest" style={{ color: isMax ? color : "#64748b" }}>
              {String(y).slice(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
}


// Home Workspace inline setup form — replaces the previous "/profile"
// redirect. Renders either the collapsed CTA button or the expanded
// two-field form. `standalone` variant is used when the card is mounted
// outside a receipt row.
function HomeWorkspaceSetup({
  user, open, setOpen, officeSqft, setOfficeSqft, totalSqft, setTotalSqft,
  saving, onSave, standalone = false, homeRows = [], onMethodChange,
}) {
  const persistedPct = Number(user?.home_office_pct || 0);
  const persistedOffice = Number(user?.home_office_sqft || 0);
  const method = String(user?.home_office_method || "simplified").toLowerCase();
  const simplified = Math.min(persistedOffice, 300) * 5;
  // Actual expenses = sum(utility totals) × pct — client-computed against
  // whatever the caller has already fetched for the home vault.
  const actualExpenses = (homeRows || []).reduce(
    (s, r) => s + Number(r?.total || 0),
    0,
  ) * persistedPct;
  return (
    <div
      className={`${standalone ? "mt-3" : "col-span-2 sm:col-span-3 mt-1"} rounded-lg border border-cyan-400/60`}
      style={{
        background: "rgba(255,255,255,0.03)",
        WebkitBackdropFilter: "blur(16px)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 0 14px rgba(6,182,212,0.35)",
      }}
      data-testid="vault-home-setup-card"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid="vault-home-setup-cta"
        className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-200">
          {persistedPct > 0
            ? `🏠 Workspace on file · ${persistedOffice} / ${Number(user?.home_total_sqft || 0)} sq ft · ${Math.round(persistedPct * 100)}% biz-use`
            : "🏠 Finish your Workspace Setup Profile to unlock home deductions"}
        </span>
        <span className="text-cyan-300 text-xs">{open ? "▾" : "→"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-cyan-400/25 space-y-2" data-testid="vault-home-setup-form">
          <div className="flex items-center gap-2" data-testid="vault-home-method-radio-group">
            {[
              { k: "simplified", label: "Simplified · $5 / sqft" },
              { k: "actual",     label: "Actual Expenses · biz-use × bills" },
            ].map(({ k, label }) => (
              <label
                key={k}
                data-testid={`vault-home-method-radio-${k}`}
                data-selected={method === k ? "true" : "false"}
                className="flex-1 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[9px] font-mono uppercase tracking-widest cursor-pointer"
                style={{
                  background: method === k ? "rgba(6,182,212,0.14)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${method === k ? "rgba(6,182,212,0.65)" : "rgba(148,163,184,0.15)"}`,
                  color: method === k ? "#67e8f9" : "#94a3b8",
                  boxShadow: method === k ? "0 0 10px rgba(6,182,212,0.35)" : undefined,
                }}
              >
                <input
                  type="radio"
                  name="home-method"
                  checked={method === k}
                  onChange={() => onMethodChange && onMethodChange(k)}
                  className="w-3 h-3"
                  style={{ accentColor: "#06B6D4" }}
                />
                {label}
              </label>
            ))}
          </div>
          <label className="block">
            <span className="block text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
              Home Office Square Footage <span className="text-cyan-300">· IRS §280A(c)(5) 300 sq ft cap</span>
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={300}
              placeholder="e.g. 150"
              value={officeSqft}
              onChange={(e) => setOfficeSqft(e.target.value)}
              data-testid="vault-home-office-sqft-input"
              className="w-full h-9 px-2.5 text-sm font-mono rounded-md outline-none"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(148,163,184,0.20)",
                color: "#e2e8f0",
              }}
            />
          </label>
          <label className="block">
            <span className="block text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
              Total Finished Home Square Footage
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="e.g. 1800"
              value={totalSqft}
              onChange={(e) => setTotalSqft(e.target.value)}
              data-testid="vault-home-total-sqft-input"
              className="w-full h-9 px-2.5 text-sm font-mono rounded-md outline-none"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(148,163,184,0.20)",
                color: "#e2e8f0",
              }}
            />
          </label>
          {officeSqft && totalSqft && Number(officeSqft) > 0 && Number(totalSqft) > 0 && (
            <div
              data-testid="vault-home-setup-preview"
              className="rounded-md p-2 text-[10px] font-mono border border-cyan-500/40"
              style={{ background: "rgba(6,182,212,0.08)", color: "#67e8f9" }}
            >
              Preview · biz-use {Math.round(Math.min(1, Number(officeSqft) / Number(totalSqft)) * 100)}%
              &nbsp;·&nbsp; simplified {"$"}
              {Math.min(Number(officeSqft), 300) * 5}
              &nbsp;/&nbsp;{"$"}1,500 cap
            </div>
          )}
          <div className="flex items-center justify-between pt-1">
            <span
              className="text-[9px] font-mono uppercase tracking-widest"
              style={{ color: persistedPct > 0 ? "#67e8f9" : "#64748b" }}
              data-testid="vault-home-simplified-display"
            >
              {persistedPct > 0
                ? (method === "actual"
                    ? `● live · actual $${actualExpenses.toFixed(2)} · ${Math.round(persistedPct * 100)}% × bills`
                    : `● live · simplified $${simplified.toFixed(2)} / $1,500`)
                : "○ awaiting sq-ft entry"}
            </span>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              data-testid="vault-home-setup-save"
              className="h-8 px-3 rounded-md text-[10px] font-mono uppercase tracking-widest disabled:opacity-40"
              style={{
                background: "rgba(6,182,212,0.20)",
                border: "1px solid rgba(6,182,212,0.65)",
                color: "#67e8f9",
                boxShadow: "0 0 10px rgba(6,182,212,0.35)",
              }}
            >
              {saving ? "Saving…" : "🏠 Save Workspace"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// CSV column chooser overlay — presented right before download so the
// accountant only gets the columns they actually asked for. Rendered once
// per vault kind because column sets differ by domain.
function CsvColumnChooser({ open, columns, color, onCancel, onConfirm }) {
  const [selected, setSelected] = useState(() => new Set(columns.map((c) => c.key)));
  useEffect(() => {
    if (open) setSelected(new Set(columns.map((c) => c.key)));
  }, [open, columns]);
  if (!open) return null;
  const toggle = (k) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const allOn = selected.size === columns.length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      data-testid="vault-csv-column-chooser"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl p-5 border"
        style={{
          borderColor: color + "80",
          background: "linear-gradient(180deg, rgba(6,25,55,0.92), rgba(15,23,42,0.95))",
          boxShadow: `0 0 32px ${color}55`,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em]" style={{ color }}>
            📥 Column Chooser · CSV export
          </div>
          <button
            type="button"
            onClick={() => (allOn ? setSelected(new Set()) : setSelected(new Set(columns.map((c) => c.key))))}
            data-testid="vault-csv-chooser-toggle-all"
            className="text-[9px] font-mono uppercase tracking-widest text-slate-400 hover:text-white"
          >
            {allOn ? "Clear all" : "Select all"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {columns.map((c) => {
            const on = selected.has(c.key);
            return (
              <label
                key={c.key}
                data-testid={`vault-csv-chooser-col-${c.key}`}
                data-selected={on ? "true" : "false"}
                className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[10px] font-mono cursor-pointer transition-colors"
                style={{
                  background: on ? `${color}18` : "rgba(255,255,255,0.02)",
                  border: `1px solid ${on ? color + "80" : "rgba(148,163,184,0.15)"}`,
                  color: on ? "#e2e8f0" : "#94a3b8",
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(c.key)}
                  className="w-3 h-3"
                  style={{ accentColor: color }}
                />
                <span className="tracking-widest uppercase">{c.label}</span>
              </label>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500" data-testid="vault-csv-chooser-count">
            {selected.size} of {columns.length} columns selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid="vault-csv-chooser-cancel"
              className="h-8 px-3 rounded-md text-[10px] font-mono uppercase tracking-widest border border-slate-600 text-slate-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(columns.filter((c) => selected.has(c.key)))}
              disabled={selected.size === 0}
              data-testid="vault-csv-chooser-confirm"
              className="h-8 px-3 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40"
              style={{
                background: `${color}28`,
                border: `1px solid ${color}`,
                color,
                boxShadow: `0 0 10px ${color}55`,
              }}
            >
              📥 Download CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// and its earliest sibling within the same signature bucket. The user can
// click either card to flip which record survives; the "loser" fades and the
// winner keeps a high-contrast emerald halo. Confirm fires the DELETE.
function DiffModal({ target, sibling, color, onCancel, onConfirm, busy }) {
  // `winnerId` tracks which document_id survives. Defaults to the sibling
  // (the earliest-uploaded row in the signature bucket) so click-to-toggle
  // flips which record the DELETE call targets.
  const defaultWinnerId = sibling?.document_id || target?.document_id || null;
  const [winnerId, setWinnerId] = useState(defaultWinnerId);
  useEffect(() => { setWinnerId(defaultWinnerId); }, [defaultWinnerId]);
  if (!target) return null;
  const rows = [
    ["Date",       target.date || "—",               sibling?.date || "—"],
    ["Merchant",   target.merchant || "—",           sibling?.merchant || "—"],
    ["Category",   target.category || "—",           sibling?.category || "—"],
    ["Amount",     fmt(target.total),                fmt(sibling?.total)],
    ["Line items", (target.line_items || []).map((li) => li.description || "").join(" · ") || "—",
                   (sibling?.line_items || []).map((li) => li.description || "").join(" · ") || "—"],
    ["Uploaded",   (target.uploaded_at || "").slice(0, 19).replace("T", " ") || "—",
                   (sibling?.uploaded_at || "").slice(0, 19).replace("T", " ") || "—"],
  ];
  const winsLeft  = winnerId === target.document_id;
  const winsRight = winnerId === (sibling?.document_id || null);
  const cardStyle = (isWinner) => ({
    background: isWinner ? "rgba(16,185,129,0.10)" : "rgba(245,158,11,0.06)",
    border: `1px solid ${isWinner ? "rgba(16,185,129,0.85)" : "rgba(148,163,184,0.15)"}`,
    boxShadow: isWinner ? "0 0 14px rgba(16,185,129,0.55), inset 0 0 0 1px rgba(16,185,129,0.55)" : undefined,
    opacity: winnerId && !isWinner ? 0.42 : 1,
    cursor: "pointer",
    transition: "opacity 180ms ease, box-shadow 220ms ease, border-color 220ms ease",
  });
  const purgeTargetId = winnerId === target.document_id
    ? (sibling?.document_id || null)
    : target.document_id;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      data-testid="vault-auto-diff-modal"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-2xl rounded-2xl p-5 border"
        onClick={(e) => e.stopPropagation()}
        style={{
          borderColor: color + "80",
          background: "linear-gradient(180deg, rgba(6,25,55,0.92), rgba(15,23,42,0.95))",
          boxShadow: `0 0 32px ${color}55, inset 0 0 0 1px rgba(255,255,255,0.04)`,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em]" style={{ color }}>
            ⚠ Duplicate Diff · Tap the record to keep
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="vault-auto-diff-close"
            className="text-slate-400 hover:text-white text-xs font-mono tracking-widest"
          >
            ✕ CLOSE
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { side: "target",  row: target,  isWinner: winsLeft,  disabled: false },
            { side: "sibling", row: sibling, isWinner: winsRight, disabled: !sibling },
          ].map(({ side, row, isWinner, disabled }, colIdx) => (
            <div
              key={side}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && row?.document_id && setWinnerId(row.document_id)}
              onKeyDown={(e) => {
                if (disabled || !row?.document_id) return;
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setWinnerId(row.document_id); }
              }}
              data-testid={`vault-auto-diff-card-${side}`}
              data-winner={isWinner ? "true" : "false"}
              className="rounded-lg p-3 text-[11px] font-mono outline-none"
              style={cardStyle(isWinner)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] uppercase tracking-widest"
                      style={{ color: isWinner ? "#6ee7b7" : "#fbbf24" }}>
                  {isWinner ? "✓ Keep this row" : "✕ Purge this row"}
                </span>
                <span className="text-[8px] font-mono text-slate-500 tracking-widest">
                  {(row?.document_id || "").slice(0, 10)}
                </span>
              </div>
              {rows.map(([k, a, b], i) => (
                <div key={k} className="flex items-start gap-2 py-1 border-t border-slate-800/60 first:border-t-0">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 w-16 shrink-0">{k}</span>
                  <span className="text-slate-100 break-all flex-1"
                        data-testid={`vault-auto-diff-${side === "target" ? "target" : "sibling"}-${i}`}>
                    {colIdx === 0 ? a : b}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500" data-testid="vault-auto-diff-purge-target">
            Will purge · {(purgeTargetId || "—").slice(0, 12)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              data-testid="vault-auto-diff-cancel"
              className="h-9 px-4 rounded-md text-[10px] font-mono uppercase tracking-widest border border-slate-600 text-slate-300 hover:text-white transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(purgeTargetId)}
              disabled={busy || !purgeTargetId}
              data-testid="vault-auto-diff-confirm"
              className="h-9 px-4 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40"
              style={{
                background: "rgba(245,158,11,0.22)",
                border: "1px solid rgba(245,158,11,0.75)",
                color: "#fbbf24",
                boxShadow: "0 0 14px rgba(245,158,11,0.45)",
              }}
            >
              {busy ? "Purging…" : "⚡ Confirm Purge"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// IRS Simplified Home-Office Method ceiling (IRC §280A(c)(1)): $5/sq ft × 300 sq ft.
const HOME_OFFICE_SIMPLIFIED_CAP = 1_500;

const VARIANTS = {
  auto:    { color: "#FF6B00", label: "🏎️ Auto Logistics Vault",       hint: "Photograph fuel / repair / tire receipts." },
  home:    { color: "#06B6D4", label: "🏡 Home Office Utility Matrix", hint: "Drop internet / power / gas bills." },
  capital: { color: "#FFD700", label: "🛠️ Capital Asset Lock Box",     hint: "Upload computer / camera / machinery invoices." },
};

function DepreciationCurve({ schedule = [], color = "#FFD700", elected = false, onToggleElection, purchasePrice = 0, bonusElected = false, onToggleBonus, aggregate179 = 0, cap179 = 1_160_000 }) {
  if (!schedule.length) return null;
  const svgW = 460, svgH = 140, pad = 24;
  // If §168(k) bonus is on (and §179 is off), replace schedule with a 60/40 curve.
  const displaySchedule = (bonusElected && !elected)
    ? [
        { year: 1, deduction: purchasePrice * 0.60 },
        { year: 2, deduction: purchasePrice * 0.20 },
        { year: 3, deduction: purchasePrice * 0.12 },
        { year: 4, deduction: purchasePrice * 0.05 },
        { year: 5, deduction: purchasePrice * 0.03 },
      ]
    : schedule;
  const max = Math.max(...displaySchedule.map((s) => s.deduction)) || 1;
  const px = (i) => pad + (i / (displaySchedule.length - 1 || 1)) * (svgW - pad * 2);
  const py = (v) => svgH - pad - (v / max) * (svgH - pad * 2);
  const path = displaySchedule.map((p, i) => `${i === 0 ? "M" : "L"}${px(i).toFixed(1)},${py(p.deduction).toFixed(1)}`).join(" ");
  const purchaseFmt = "$" + Number(purchasePrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const overCap = elected && aggregate179 > cap179;
  const capPct = Math.min(100, Math.round((aggregate179 / cap179) * 100));
  return (
    <div className="mt-3 rounded-lg p-3 border" style={{ borderColor: color + "60", background: "rgba(15,23,42,0.55)" }}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color }}>
          5-Year MACRS Depreciation · IRC § 179 + § 168
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => onToggleElection && onToggleElection(!elected)}
            role="switch"
            aria-checked={elected}
            data-testid="vault-capital-179-election-toggle"
            className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-widest"
          >
            <span style={{ color: elected ? "#FFD700" : "#94a3b8" }}>§ 179 Election</span>
            <span className="relative w-11 h-6 rounded-full transition-colors"
              style={{
                background: elected ? "rgba(255,215,0,0.35)" : "rgba(148,163,184,0.18)",
                border: `1px solid ${elected ? "rgba(255,215,0,0.85)" : "rgba(148,163,184,0.35)"}`,
                boxShadow: elected ? "0 0 12px rgba(255,215,0,0.6)" : undefined,
              }}>
              <span className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
                style={{
                  left: elected ? "22px" : "2px",
                  background: elected ? "radial-gradient(circle at 30% 30%, #fff9c4, #FFD700)" : "radial-gradient(circle at 30% 30%, #cbd5e1, #64748b)",
                  boxShadow: elected ? "0 0 8px #FFD700" : "0 0 4px rgba(148,163,184,0.5)",
                }} />
            </span>
          </button>
          <button
            type="button"
            onClick={() => onToggleBonus && onToggleBonus(!bonusElected)}
            role="switch"
            aria-checked={bonusElected}
            data-testid="vault-capital-168k-toggle"
            disabled={elected}
            title={elected ? "Turn §179 off to use §168(k)" : ""}
            className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-widest disabled:opacity-40"
          >
            <span style={{ color: bonusElected && !elected ? "#22d3ee" : "#94a3b8" }}>§ 168(k) Bonus</span>
            <span className="relative w-11 h-6 rounded-full transition-colors"
              style={{
                background: bonusElected && !elected ? "rgba(34,211,238,0.35)" : "rgba(148,163,184,0.18)",
                border: `1px solid ${bonusElected && !elected ? "rgba(34,211,238,0.85)" : "rgba(148,163,184,0.35)"}`,
                boxShadow: bonusElected && !elected ? "0 0 12px rgba(34,211,238,0.6)" : undefined,
              }}>
              <span className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
                style={{
                  left: bonusElected ? "22px" : "2px",
                  background: bonusElected && !elected ? "radial-gradient(circle at 30% 30%, #cffafe, #22d3ee)" : "radial-gradient(circle at 30% 30%, #cbd5e1, #64748b)",
                  boxShadow: bonusElected && !elected ? "0 0 8px #22d3ee" : "0 0 4px rgba(148,163,184,0.5)",
                }} />
            </span>
          </button>
        </div>
      </div>
      {elected && (
        <div
          data-testid="vault-capital-179-cap-strip"
          className={`mb-2 rounded-md px-2 py-1 text-[9px] font-mono uppercase tracking-widest border ${overCap ? "" : ""}`}
          style={{
            background: overCap ? "rgba(245,158,11,0.10)" : "rgba(255,215,0,0.06)",
            borderColor: overCap ? "rgba(245,158,11,0.65)" : "rgba(255,215,0,0.30)",
            color: overCap ? "#fbbf24" : "#fde68a",
            boxShadow: overCap ? "0 0 10px rgba(245,158,11,0.35)" : undefined,
          }}
        >
          §179 aggregate · ${Math.round(aggregate179).toLocaleString()} / ${Math.round(cap179).toLocaleString()} ({capPct}%)
          {overCap && <span data-testid="vault-capital-179-cap-warning"> ⚠ over statutory ceiling</span>}
        </div>
      )}
      {elected ? (
        <div
          data-testid="vault-capital-179-badge"
          className="rounded-lg py-4 text-center font-mono"
          style={{
            background: "linear-gradient(90deg, rgba(255,215,0,0.20), rgba(255,176,0,0.14), rgba(255,215,0,0.20))",
            border: "1px solid rgba(255,215,0,0.75)",
            boxShadow: "0 0 22px rgba(255,215,0,0.55), inset 0 0 12px rgba(255,215,0,0.25)",
            color: "#FFD700",
            textShadow: "0 0 10px rgba(255,215,0,0.85)",
            animation: "section-179-pulse 1.6s ease-in-out infinite",
          }}
        >
          <div className="text-[11px] uppercase tracking-[0.32em] font-bold">
            🔥 Section 179 Elected · 100% Current Year Write-Off
          </div>
          <div className="text-[10px] mt-1" style={{ color: "#fde68a" }}>
            {purchaseFmt} routed into Schedule C · Line 13 · TY current
          </div>
        </div>
      ) : (
        <>
          {bonusElected && (
            <div
              data-testid="vault-capital-168k-badge"
              className="mb-2 rounded-md px-2 py-1 text-[9px] font-mono uppercase tracking-widest"
              style={{
                background: "rgba(34,211,238,0.10)",
                border: "1px solid rgba(34,211,238,0.55)",
                color: "#67e8f9",
                boxShadow: "0 0 10px rgba(34,211,238,0.35)",
              }}
            >
              ⚡ § 168(k) Bonus · 60% Y1 accelerated · MACRS curve retuned
            </div>
          )}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto" data-testid="capital-depreciation-curve">
            <path d={path} fill="none" stroke={bonusElected ? "#22d3ee" : color} strokeWidth="2" />
            {displaySchedule.map((s, i) => (
              <g key={s.year}>
                <circle cx={px(i)} cy={py(s.deduction)} r="3" fill={bonusElected ? "#22d3ee" : color} />
                <text x={px(i)} y={py(s.deduction) - 6} textAnchor="middle" fontSize="9" fill={bonusElected ? "#22d3ee" : color} fontFamily="ui-monospace, monospace">
                  Yr {s.year} · {fmt(s.deduction)}
                </text>
              </g>
            ))}
          </svg>
        </>
      )}
      <style>{`
        @keyframes section-179-pulse {
          0%, 100% { box-shadow: 0 0 18px rgba(255,215,0,0.45), inset 0 0 10px rgba(255,215,0,0.20); }
          50%      { box-shadow: 0 0 32px rgba(255,215,0,0.85), inset 0 0 16px rgba(255,215,0,0.40); }
        }
      `}</style>
    </div>
  );
}

export function SpecializedVault({ kind }) {
  const cfg = VARIANTS[kind];
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [doc, setDoc] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  // Auto-vault only — mileage business-use ratio drives the IRC §162 blend.
  const [businessUsePct, setBusinessUsePct] = useState(0);
  const [mileageMeta, setMileageMeta] = useState({ business_miles: 0, total_miles: 0 });
  // Annualized Auto Cost Blender — sum every persisted auto receipt × biz-use%.
  const [autoAnnualized, setAutoAnnualized] = useState({ gross: 0, deduction: 0, count: 0 });
  const [autoRows, setAutoRows] = useState([]);
  const [autoFilter, setAutoFilter] = useState("all"); // all | fuel | maintenance_registration
  const [autoSearch, setAutoSearch] = useState("");
  // Home + Capital rows drive the sparklines in their respective headers.
  const [homeRows, setHomeRows] = useState([]);
  const [capitalRows, setCapitalRows] = useState([]);
  // Multi-year slice glider — persists in localStorage so the IRS packet reads it.
  const [taxYear, setTaxYear] = useState(readTaxYear());
  // Merge & Purge state — set to the document_id currently being deleted.
  const [purging, setPurging] = useState(null);
  // Diff modal state — the row the user clicked "Merge & Purge" on, plus its
  // sibling (earliest same-signature row that survives).
  const [pendingDiff, setPendingDiff] = useState(null); // { target, sibling }
  // Bulk-select purge state — set of document_ids the user has ticked.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPurging, setBulkPurging] = useState(false);
  // CSV column chooser — one gate per vault instance. Payload holds the
  // rows to export + the full column catalogue + a download builder that
  // runs once the user confirms their column selection.
  const [csvGate, setCsvGate] = useState(null);
  // Home workspace onboarding — inline form state (open/office/home).
  const [homeSetupOpen, setHomeSetupOpen] = useState(false);
  const [homeOfficeSqft, setHomeOfficeSqft] = useState("");
  const [homeTotalSqft, setHomeTotalSqft] = useState("");
  const [homeSetupSaving, setHomeSetupSaving] = useState(false);
  // Section 179 election flag per document_id — flipping ON pipes the full
  // purchase_price into Line 13 of the Schedule C bridge instead of the Y1
  // MACRS number, and swaps the depreciation chart for a pulsing gold badge.
  const [section179Elected, setSection179Elected] = useState({});
  // IRC §168(k) bonus depreciation election flag per document_id — mutually
  // exclusive with §179 on the client (§179 wins when both are on).
  const [section168kElected, setSection168kElected] = useState({});
  // Multi-Vault Workspace Cleaner — dual-action wipe (state + backend rows).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  // §179 aggregate + statutory cap for the amber cap-guard indicator.
  const [aggregate179, setAggregate179] = useState(0);
  const CAP_179 = 1_160_000;
  useEffect(() => {
    if (kind !== "capital") return;
    const total = (capitalRows || []).reduce(
      (s, r) => s + (r?.section_179_elected ? Number(r.purchase_price || 0) : 0),
      0,
    );
    setAggregate179(total);
  }, [kind, capitalRows]);
  // Seed the local election maps from persisted rows so the toggles reflect
  // the last-saved state on mount.
  useEffect(() => {
    if (kind !== "capital") return;
    const seed179 = {}, seed168 = {};
    (capitalRows || []).forEach((r) => {
      if (r?.document_id && r?.section_179_elected) seed179[r.document_id] = true;
      if (r?.document_id && r?.section_168k_elected) seed168[r.document_id] = true;
    });
    if (doc?.document_id && doc?.section_179_elected) seed179[doc.document_id] = true;
    if (doc?.document_id && doc?.section_168k_elected) seed168[doc.document_id] = true;
    setSection179Elected((prev) => ({ ...seed179, ...prev }));
    setSection168kElected((prev) => ({ ...seed168, ...prev }));
  }, [kind, capitalRows, doc]);
  const toggleSection179 = async (docId, purchasePrice, next) => {
    if (!docId) return;
    setSection179Elected((prev) => ({ ...prev, [docId]: next }));
    // Optimistic aggregate update — server response finalises it.
    setAggregate179((prev) => prev + (next ? Number(purchasePrice || 0) : -Number(purchasePrice || 0)));
    try {
      const res = await api.patch(`/v1/ingest/vault/capital/${docId}/section-179`, { elected: !!next });
      if (res?.data?.aggregate_179_elected != null) {
        setAggregate179(Number(res.data.aggregate_179_elected));
      }
      toast.success(next
        ? `🔥 Section 179 elected · $${Number(purchasePrice || 0).toLocaleString()} routed to Line 13`
        : "MACRS depreciation restored");
    } catch (e) {
      setSection179Elected((prev) => ({ ...prev, [docId]: !next }));
      setAggregate179((prev) => prev + (next ? -Number(purchasePrice || 0) : Number(purchasePrice || 0)));
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };
  const toggleSection168k = async (docId, next) => {
    if (!docId) return;
    setSection168kElected((prev) => ({ ...prev, [docId]: next }));
    try {
      await api.patch(`/v1/ingest/vault/capital/${docId}/section-168k`, { elected: !!next });
      toast.success(next ? "⚡ § 168(k) bonus depreciation ON · 60% Y1 write-off" : "Bonus depreciation OFF");
    } catch (e) {
      setSection168kElected((prev) => ({ ...prev, [docId]: !next }));
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };
  useEffect(() => {
    if (kind !== "home") return;
    if (user?.home_office_sqft) setHomeOfficeSqft(String(user.home_office_sqft));
    if (user?.home_total_sqft) setHomeTotalSqft(String(user.home_total_sqft));
  }, [kind, user?.home_office_sqft, user?.home_total_sqft]);

  useEffect(() => { writeTaxYear(taxYear); }, [taxYear]);

  const refetchAuto = async () => {
    try {
      const aRes = await api.get("/v1/ingest/vault/auto?limit=200");
      const rows = aRes?.data?.docs || aRes?.data?.documents || [];
      setAutoRows(rows);
      return rows;
    } catch { return []; }
  };

  useEffect(() => {
    if (kind !== "auto") return;
    let cancelled = false;
    Promise.all([
      api.get("/mileage").catch(() => null),
      api.get("/v1/ingest/vault/auto?limit=200").catch(() => null),
    ]).then(([mRes, aRes]) => {
        if (cancelled) return;
        const m = mRes?.data || {};
        const total = Number(m.total_miles || 0);
        let biz = Number(m.business_miles || 0);
        if (!biz && Number(m.business_mileage_deduction || 0) && Number(m.total_deduction || 0)) {
          biz = total * (Number(m.business_mileage_deduction) / Number(m.total_deduction));
        }
        const pct = total > 0 ? Math.min(1, Math.max(0, biz / total)) : 1;
        setBusinessUsePct(pct);
        setMileageMeta({ business_miles: Math.round(biz), total_miles: Math.round(total) });
        // Annualized blend — sum all persisted receipts (fuel/tire/maintenance/registration
        // are already classified into the auto vault at ingest time). Deduction = gross × pct.
        const rows = aRes?.data?.docs || aRes?.data?.documents || [];
        setAutoRows(rows);
        const gross = rows.reduce((s, d) => s + Number(d.total || 0), 0);
        setAutoAnnualized({
          gross: Math.round(gross * 100) / 100,
          deduction: Math.round(gross * pct * 100) / 100,
          count: rows.length,
        });
      })
      .catch(() => { /* leave defaults — non-blocking */ });
    return () => { cancelled = true; };
  }, [kind]);

  // Home + Capital: fetch persisted rows for sparkline rendering.
  useEffect(() => {
    if (kind !== "home" && kind !== "capital") return;
    let cancelled = false;
    api.get(`/v1/ingest/vault/${kind}?limit=200`)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.docs || res?.data?.documents || [];
        if (kind === "home") setHomeRows(rows);
        else setCapitalRows(rows);
      })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [kind]);

  const onFile = async (raw) => {
    if (!raw || busy) return;
    setPreview(URL.createObjectURL(raw));
    setSweeping(true);
    setBusy(true);
    const toastId = toast.loading(`${cfg.label} · scanning…`);
    try {
      const form = new FormData();
      form.append("file", raw);
      const { data } = await api.post(`/v1/ingest/vault/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc(data?.document || null);
      toast.success(`${cfg.label} · ingested`, { id: toastId });
      // Refresh dashboard stats so Cash Flash metrics update instantly.
      window.dispatchEvent(new CustomEvent("receipts-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ingestion failed", { id: toastId });
    } finally {
      setBusy(false);
      setTimeout(() => setSweeping(false), 1500);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!busy) setIsDragOver(true); };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); if (!busy) setIsDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (busy) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  const performVaultReset = async () => {
    if (resetBusy) return;
    setResetBusy(true);
    const toastId = toast.loading(`♻️ Clearing ${cfg.label} · ${taxYear} slice…`);
    try {
      // Backend: wipe rows of this vault kind for the caller, scoped to the
      // currently-active Multi-Year Slice year so alternate historical years
      // stay intact.
      await api.delete(`/v1/ingest/vault/${kind}/all?year=${taxYear}`).catch(() => null);
      // Frontend: flush local hooks. Refetch after so alternate-year rows
      // rehydrate immediately.
      setDoc(null);
      setPreview(null);
      setSweeping(false);
      setBusy(false);
      setSelectedIds(new Set());
      setPendingDiff(null);
      setAutoSearch("");
      setAutoFilter("all");
      if (kind === "auto") {
        const rows = await refetchAuto();
        setAutoRows(rows);
        // Rebuild annualized blender from surviving rows only.
        const surviving = (rows || []).reduce((s, r) => s + Number(r.total || 0), 0);
        setAutoAnnualized({ gross: surviving, deduction: surviving * (businessUsePct || 1), count: rows.length });
      } else if (kind === "home") {
        try {
          const r = await api.get("/v1/ingest/vault/home?limit=200");
          setHomeRows(r?.data?.docs || r?.data?.documents || []);
        } catch { setHomeRows([]); }
        // If the user wiped the current year AND has no rows in other years,
        // reset the workspace onboarding fields too.
      } else if (kind === "capital") {
        try {
          const r = await api.get("/v1/ingest/vault/capital?limit=200");
          const rows = r?.data?.docs || r?.data?.documents || [];
          setCapitalRows(rows);
          // Prune §179/§168k election maps for rows that no longer exist.
          const surviving = new Set(rows.map((x) => x.document_id));
          setSection179Elected((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => surviving.has(id))));
          setSection168kElected((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => surviving.has(id))));
          const total = rows.reduce((s, r2) => s + (r2?.section_179_elected ? Number(r2.purchase_price || 0) : 0), 0);
          setAggregate179(total);
        } catch { setCapitalRows([]); }
      }
      if (fileRef.current) fileRef.current.value = "";
      toast.success(`${cfg.label} · ${taxYear} slice cleared`, { id: toastId });
      // Notify the Control Deck audit strip to refetch.
      window.dispatchEvent(new CustomEvent("reset-audit-log-updated"));
      setResetOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed", { id: toastId });
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div
      data-testid={`vault-${kind}`}
      className="relative rounded-2xl p-5 vault-halo"
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        background: "linear-gradient(180deg, rgba(6,25,55,0.85), rgba(15,23,42,0.9))",
        border: isDragOver ? `1px solid ${cfg.color}` : `1px solid ${cfg.color}80`,
        boxShadow: isDragOver ? `0 0 28px -4px ${cfg.color}80, inset 0 0 32px -8px ${cfg.color}22` : undefined,
        transition: "border-color 180ms ease, box-shadow 180ms ease",
        ["--vault-color"]: cfg.color,
      }}
    >
      {isDragOver && (
        <div
          data-testid={`vault-${kind}-drag-overlay`}
          className="absolute inset-0 rounded-2xl pointer-events-none flex items-center justify-center z-10"
          style={{ background: `${cfg.color}12`, backdropFilter: "blur(2px)" }}
        >
          <span
            className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] px-3 py-1.5 rounded-lg"
            style={{ color: cfg.color, background: `${cfg.color}22`, border: `1px solid ${cfg.color}60` }}
          >
            Drop to scan
          </span>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: cfg.color }}>
          {cfg.label}
        </div>
        <div className="flex items-center gap-2">
          <Sparkline
            rows={(kind === "auto" ? autoRows : kind === "home" ? homeRows : capitalRows).filter((r) => rowMatchesYear(r, taxYear))}
            color={cfg.color}
            testid={`vault-${kind}-sparkline`}
          />
          <YoYBars
            rows={kind === "auto" ? autoRows : kind === "home" ? homeRows : capitalRows}
            color={cfg.color}
            testid={`vault-${kind}-yoy-bars`}
            valueKey={kind === "capital" ? "purchase_price" : "total"}
          />
          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 audit-safe-glow">
            <ShieldCheck size={9} /> Audit-Safe
          </span>
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            data-testid={`vault-${kind}-reset-btn`}
            className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:text-white hover:bg-rose-500/25 transition-colors"
            title={`Wipe every ${cfg.label} row + reset workspace back to onboarding`}
          >
            <RotateCcw size={9} /> Clear Slice
          </button>
        </div>
      </div>
      <div
        data-testid={`vault-${kind}-year-glider`}
        className="mb-3 rounded-lg px-3 py-2 border"
        style={{
          borderColor: cfg.color + "40",
          background: "rgba(15,23,42,0.55)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
            Multi-Year Slice · Historic Ledger
          </span>
          <span
            className="text-[10px] font-mono font-bold"
            style={{ color: cfg.color, textShadow: `0 0 8px ${cfg.color}80` }}
            data-testid={`vault-${kind}-year-value`}
          >
            {taxYear}
          </span>
        </div>
        <input
          type="range"
          min={TAX_YEARS[0]}
          max={TAX_YEARS[TAX_YEARS.length - 1]}
          step={1}
          value={taxYear}
          onChange={(e) => setTaxYear(Number(e.target.value))}
          data-testid={`vault-${kind}-year-slider`}
          className="w-full h-1 appearance-none rounded-full outline-none"
          style={{
            background: `linear-gradient(90deg, ${cfg.color}66 0%, ${cfg.color} ${((taxYear - TAX_YEARS[0]) / (TAX_YEARS.length - 1)) * 100}%, rgba(148,163,184,0.15) ${((taxYear - TAX_YEARS[0]) / (TAX_YEARS.length - 1)) * 100}%)`,
            accentColor: cfg.color,
          }}
        />
        <div className="flex justify-between mt-1">
          {TAX_YEARS.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setTaxYear(y)}
              data-testid={`vault-${kind}-year-chip-${y}`}
              className="text-[8px] font-mono tracking-widest transition-colors"
              style={{
                color: taxYear === y ? cfg.color : "#64748b",
                textShadow: taxYear === y ? `0 0 6px ${cfg.color}80` : undefined,
              }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid={`vault-${kind}-upload-btn`}
        className={`w-full h-12 rounded-xl text-slate-950 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-transform ${busy ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
        style={{ background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}b3)`, boxShadow: `0 10px 22px -10px ${cfg.color}` }}
        title="Click to browse · or drag &amp; drop a file anywhere on this tile"
      >
        <Upload size={13} /> {busy ? "Scanning…" : cfg.hint}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid={`vault-${kind}-file-input`}
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      {preview && (
        <div className="mt-3 relative overflow-hidden rounded-xl border" style={{ borderColor: cfg.color + "40" }}>
          <img src={preview} alt="Upload" className="w-full max-h-[220px] object-contain bg-slate-950/60" />
          {sweeping && (
            <div
              data-testid={`vault-${kind}-laser-sweep`}
              className="absolute inset-x-0 h-1 pointer-events-none"
              style={{
                top: 0,
                background: "linear-gradient(90deg, transparent, #fff, transparent)",
                boxShadow: "0 0 22px 6px rgba(255,255,255,0.9)",
                animation: "vault-laser-sweep 1.5s cubic-bezier(.22,1,.36,1) forwards",
              }}
            />
          )}
        </div>
      )}
      {kind === "home" && (
        <HomeWorkspaceSetup
          standalone
          user={user}
          homeRows={homeRows}
          onMethodChange={async (m) => {
            try {
              await api.patch("/auth/home-method", { method: m });
              await checkAuth();
              toast.success(`Home method · ${m === "actual" ? "Actual Expenses" : "Simplified"}`);
            } catch { toast.error("Save failed"); }
          }}
          open={homeSetupOpen}
          officeSqft={homeOfficeSqft}
          setOfficeSqft={setHomeOfficeSqft}
          totalSqft={homeTotalSqft}
          setTotalSqft={setHomeTotalSqft}
          saving={homeSetupSaving}
          setOpen={setHomeSetupOpen}
          onSave={async () => {
            const off = Number(homeOfficeSqft);
            const tot = Number(homeTotalSqft);
            if (!(off > 0) || !(tot > 0)) { toast.error("Enter both sq-ft values"); return; }
            if (off > tot) { toast.error("Office sq-ft cannot exceed home sq-ft"); return; }
            setHomeSetupSaving(true);
            const toastId = toast.loading("Saving workspace…");
            try {
              await api.patch("/auth/home-workspace", { office_sqft: off, home_sqft: tot });
              await checkAuth();
              const res = await api.get("/v1/ingest/vault/home?limit=200");
              setHomeRows(res?.data?.docs || res?.data?.documents || []);
              toast.success("🏠 Workspace saved · deductions live", { id: toastId });
              setHomeSetupOpen(false);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Save failed", { id: toastId });
            } finally { setHomeSetupSaving(false); }
          }}
        />
      )}
      {(kind === "home" || kind === "capital") && (() => {
        const rows = (kind === "home" ? homeRows : capitalRows).filter((r) => rowMatchesYear(r, taxYear));
        const HOME_COLS = [
          { key: "provider",           label: "Provider",       val: (r) => r.provider || "" },
          { key: "utility_type",       label: "Utility",        val: (r) => r.utility_type || "" },
          { key: "date",               label: "Date",           val: (r) => r.date || "" },
          { key: "total",              label: "Total",          val: (r) => Number(r.total || 0).toFixed(2) },
          { key: "home_office_pct",    label: "H-O %",          val: (r) => Math.round(Number(r.home_office_pct || 0) * 100) + "%" },
          { key: "deductible_amount",  label: "Deductible",     val: (r) => Number(r.deductible_amount || 0).toFixed(2) },
        ];
        const CAP_COLS = [
          { key: "asset_name",         label: "Asset",          val: (r) => r.asset_name || "" },
          { key: "asset_class",        label: "Class",          val: (r) => r.asset_class || "" },
          { key: "serial_number",      label: "Serial",         val: (r) => r.serial_number || "" },
          { key: "date",               label: "Date",           val: (r) => r.date || "" },
          { key: "purchase_price",     label: "Price",          val: (r) => Number(r.purchase_price || 0).toFixed(2) },
          { key: "year1_macrs",        label: "Yr-1 MACRS",     val: (r) => Number(r.depreciation_schedule?.[0]?.deduction || 0).toFixed(2) },
          { key: "five_year_total",    label: "5-Yr Total",     val: (r) => ((r.depreciation_schedule || []).reduce((s, x) => s + Number(x.deduction || 0), 0)).toFixed(2) },
        ];
        const cols = kind === "home" ? HOME_COLS : CAP_COLS;
        const exportKindCsv = () => {
          setCsvGate({
            kind,
            color: cfg.color,
            columns: cols,
            build: (picked) => {
              const escape = (v) => {
                const s = String(v ?? "");
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
              };
              const header = picked.map((c) => c.key);
              const body = rows.map((r) => picked.map((c) => c.val(r)));
              const csv = [header, ...body].map((r) => r.map(escape).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              const stamp = new Date().toISOString().slice(0, 10);
              a.href = url;
              a.download = `${kind}_vault_${taxYear}_${stamp}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              try { toast.success(`Exported ${body.length} ${kind} rows · ${picked.length} cols · TY${taxYear}`); } catch { /* noop */ }
            },
          });
        };
        return (
          <div
            className="mb-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 border"
            style={{
              borderColor: cfg.color + "40",
              background: "rgba(15,23,42,0.55)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}
            data-testid={`vault-${kind}-csv-strip`}
          >
            <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
              TY {taxYear} · {rows.length} row{rows.length === 1 ? "" : "s"} in slice
            </span>
            <button
              type="button"
              onClick={exportKindCsv}
              disabled={rows.length === 0}
              data-testid={`vault-${kind}-csv-export-btn`}
              className="h-8 px-3 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40"
              style={{
                background: `${cfg.color}22`,
                border: `1px solid ${cfg.color}80`,
                color: cfg.color,
                boxShadow: `0 0 10px ${cfg.color}55`,
              }}
            >
              📥 Export CSV
            </button>
          </div>
        );
      })()}
      {kind === "auto" && autoRows.length > 0 && (() => {
        const cat = (r) => String(r.category || "").toLowerCase();
        const isFuel = (r) => /fuel|gas|petrol|charge|charging/.test(cat(r));
        const isMR   = (r) => /maint|repair|tire|service|oil|brake|registration|dmv|reg\b/.test(cat(r));
        const q = autoSearch.trim().toLowerCase();
        const matchesQuery = (r) => {
          if (!q) return true;
          const hay = [
            r.merchant, r.category, r.notes, r.date,
            ...(Array.isArray(r.line_items) ? r.line_items.map((li) => `${li.description || ""} ${li.amount || ""}`) : []),
          ].join(" ").toLowerCase();
          return hay.includes(q);
        };
        // Year-glider filter runs first — everything downstream (chips, search,
        // duplicate-guard, CSV export, Schedule C bridge) reads this sliced set.
        const yearRows = autoRows.filter((r) => rowMatchesYear(r, taxYear));
        const filtered = yearRows.filter((r) =>
          (autoFilter === "fuel" ? isFuel(r)
            : autoFilter === "maintenance_registration" ? isMR(r)
            : true)
          && matchesQuery(r)
        );
        const grossF = filtered.reduce((s, r) => s + Number(r.total || 0), 0);
        const dedF   = Math.round(grossF * businessUsePct * 100) / 100;
        const label  = autoFilter === "fuel" ? "fuel-only" : autoFilter === "maintenance_registration" ? "maint · registration" : "all receipts";
        // Duplicate-guard: hash by (merchant|total|date). Duplicates are scored
        // WITHIN the year slice — swapping years re-computes the buckets.
        const dupSig = (r) => `${String(r.merchant || "").trim().toLowerCase()}|${Number(r.total || 0).toFixed(2)}|${r.date || ""}`;
        const sigCounts = yearRows.reduce((acc, r) => {
          const s = dupSig(r);
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});
        const isDup = (r) => (sigCounts[dupSig(r)] || 0) > 1;
        const dupCount = filtered.filter(isDup).length;
        // Merge & Purge Duplicate — instead of deleting straight away, open the
        // diff modal so the user confirms which row survives. The earliest
        // (oldest uploaded_at) sibling in the same signature bucket is the
        // canonical winner.
        const openDiff = (row) => {
          if (!row?.document_id || purging) return;
          const sig = dupSig(row);
          const siblings = yearRows
            .filter((r) => dupSig(r) === sig && r.document_id !== row.document_id)
            .sort((a, b) => String(a.uploaded_at || "").localeCompare(String(b.uploaded_at || "")));
          setPendingDiff({ target: row, sibling: siblings[0] || null });
        };
        // Bulk-select purge — delete every ticked row in one sweep. Header
        // master checkbox reflects "all duplicates selected" state so managers
        // can one-click sweep the entire flagged pile.
        const dupIdsInSlice = filtered.filter(isDup).map((r) => r.document_id).filter(Boolean);
        const allDupSelected = dupIdsInSlice.length > 0 && dupIdsInSlice.every((id) => selectedIds.has(id));
        const toggleRowSelected = (id) => {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          });
        };
        const toggleMasterSelected = () => {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allDupSelected) dupIdsInSlice.forEach((id) => next.delete(id));
            else dupIdsInSlice.forEach((id) => next.add(id));
            return next;
          });
        };
        const bulkPurge = async () => {
          const ids = Array.from(selectedIds);
          if (!ids.length || bulkPurging) return;
          // Snapshot the full row objects BEFORE deleting so the undo toast
          // can POST them back verbatim if the manager taps it in time.
          const snapshot = filtered.filter((r) => r.document_id && selectedIds.has(r.document_id));
          setBulkPurging(true);
          const toastId = toast.loading(`Bulk-purging ${ids.length} row${ids.length === 1 ? "" : "s"}…`);
          let purged = 0;
          for (const id of ids) {
            try {
              await api.delete(`/v1/ingest/vault/auto/${id}`);
              purged += 1;
            } catch { /* keep going through the batch */ }
          }
          await refetchAuto();
          setSelectedIds(new Set());
          setBulkPurging(false);
          toast.dismiss(toastId);
          // Fire the 5-second Undo toast — tapping restores the snapshot in
          // one POST.
          toast(`⚡ Purged ${purged}/${ids.length} rows`, {
            duration: 5000,
            action: {
              label: "✍️ Undo Purge",
              onClick: async () => {
                const undoId = toast.loading(`Restoring ${snapshot.length} row${snapshot.length === 1 ? "" : "s"}…`);
                try {
                  const { data } = await api.post("/v1/ingest/vault/auto/restore", { rows: snapshot });
                  await refetchAuto();
                  toast.success(`Restored ${data?.restored ?? snapshot.length} rows`, { id: undoId });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Undo failed", { id: undoId });
                }
              },
            },
          });
        };
        // Bridge the currently-selected slice to the IRS packet compiler.
        // W2IngestPortal reads this on every packet-generate to hydrate lines
        // 9 (car & truck), 13 (§179/MACRS), and 25 (utilities). Sits on window
        // to avoid an extra API round-trip during PDF compilation.
        if (typeof window !== "undefined") {
          window.__rs_vault_slice = {
            ...(window.__rs_vault_slice || {}),
            taxYear,
            auto: {
              filter: autoFilter,
              query: autoSearch,
              rowIds: filtered.map((r) => r.document_id).filter(Boolean),
              gross: Math.round(grossF * 100) / 100,
              deduction: dedF,
            },
          };
        }
        const AUTO_COLUMNS = [
          { key: "merchant",           label: "Merchant",     val: (r) => r.merchant || "" },
          { key: "category",           label: "Category",     val: (r) => r.category || "" },
          { key: "date",               label: "Date",         val: (r) => r.date || "" },
          { key: "total",              label: "Total",        val: (r) => Number(r.total || 0).toFixed(2) },
          { key: "business_use_pct",   label: "Biz-Use %",    val: () => Math.round(businessUsePct * 100) + "%" },
          { key: "deductible_amount",  label: "Deductible",   val: (r) => (Math.round(Number(r.total || 0) * businessUsePct * 100) / 100).toFixed(2) },
        ];
        const exportCsv = () => {
          setCsvGate({
            kind: "auto",
            color: cfg.color,
            columns: AUTO_COLUMNS,
            build: (picked) => {
              const escape = (v) => {
                const s = String(v ?? "");
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
              };
              const header = picked.map((c) => c.key);
              const body = filtered.map((r) => picked.map((c) => c.val(r)));
              const csv = [header, ...body].map((r) => r.map(escape).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              const stamp = new Date().toISOString().slice(0, 10);
              a.href = url;
              a.download = `auto_vault_${autoFilter}_${taxYear}_${stamp}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              try {
                toast.success(`Exported ${body.length} rows · ${picked.length} cols · TY${taxYear}`);
              } catch { /* toast optional */ }
            },
          });
        };
        return (
          <div
            className="mt-3 rounded-lg p-3 border"
            style={{ borderColor: cfg.color + "60", background: "rgba(15,23,42,0.55)" }}
            data-testid="vault-auto-annualized-blender"
          >
            <div className="text-[9px] font-mono uppercase tracking-widest mb-2" style={{ color: cfg.color }}>
              Annualized Cost Blender · IRC §162 slice · TY {taxYear}
            </div>
            <div
              className="flex flex-wrap items-center gap-1.5 mb-2"
              data-testid="vault-auto-filter-chips"
            >
              {[
                { k: "all", label: "All Receipts" },
                { k: "fuel", label: "Fuel-Only" },
                { k: "maintenance_registration", label: "Maintenance/Registration" },
              ].map(({ k, label: L }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setAutoFilter(k)}
                  data-testid={`vault-auto-filter-${k}`}
                  className="px-2.5 h-7 rounded-full text-[9px] font-mono uppercase tracking-widest transition-colors"
                  style={{
                    background: autoFilter === k ? "rgba(16,185,129,0.18)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${autoFilter === k ? "rgba(16,185,129,0.65)" : "rgba(148,163,184,0.15)"}`,
                    color: autoFilter === k ? "#6ee7b7" : "#94a3b8",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow: autoFilter === k ? "0 0 12px rgba(16,185,129,0.35)" : undefined,
                  }}
                >
                  {L}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <input
                type="text"
                value={autoSearch}
                onChange={(e) => setAutoSearch(e.target.value)}
                placeholder="Search merchant or line-item …"
                data-testid="vault-auto-search-input"
                className="flex-1 min-w-[180px] h-8 px-2.5 text-[11px] font-mono rounded-md outline-none"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(148,163,184,0.20)",
                  color: "#e2e8f0",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              />
              <button
                type="button"
                onClick={exportCsv}
                data-testid="vault-auto-csv-export-btn"
                disabled={filtered.length === 0}
                className="h-8 px-3 rounded-md text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40"
                style={{
                  background: "rgba(16,185,129,0.14)",
                  border: "1px solid rgba(16,185,129,0.55)",
                  color: "#6ee7b7",
                  boxShadow: "0 0 10px rgba(16,185,129,0.30)",
                }}
              >
                📥 Export CSV
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <Cell k={`Gross · ${filtered.length} receipts · ${label}`} v={fmt(grossF)} />
              <Cell
                k={`Annualized Business Deduction · biz-use ${Math.round(businessUsePct * 100)}%`}
                v={fmt(dedF)}
                highlight="#10B981"
                testid="vault-auto-annualized-deduction"
              />
            </div>
            <div
              data-testid="vault-auto-fleet-target-badge"
              className="mt-2 flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-[10px] font-mono uppercase tracking-widest"
              style={{
                background: "rgba(15,23,42,0.55)",
                border: `1px solid ${cfg.color}66`,
                color: cfg.color,
                boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.04), 0 0 10px ${cfg.color}33`,
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                letterSpacing: "0.18em",
              }}
            >
              🎯 Annualized {taxYear} Fleet Target
              <span className="tabular-nums font-bold" style={{ color: "#e2e8f0" }}>
                {fmt(dedF)}
              </span>
              {(() => {
                const budget = Number(user?.annual_fleet_budget || 0);
                if (!budget) return (
                  <button
                    type="button"
                    data-testid="vault-auto-fleet-budget-set"
                    onClick={async () => {
                      const v = prompt("Set annual fleet deduction budget ($)");
                      if (v == null) return;
                      const num = Number(v);
                      if (!(num > 0)) return toast.error("Budget must be positive");
                      try {
                        await api.patch("/auth/fleet-budget", { annual_fleet_budget: num });
                        await checkAuth();
                        toast.success(`🎯 Budget set · $${num.toLocaleString()}`);
                      } catch { toast.error("Save failed"); }
                    }}
                    className="ml-1 px-2 py-0.5 rounded-full text-[9px] tracking-widest"
                    style={{ background: "rgba(148,163,184,0.14)", border: "1px solid rgba(148,163,184,0.35)", color: "#94a3b8" }}
                  >
                    + Set Budget
                  </button>
                );
                const delta = dedF - budget;
                const under = delta < 0;
                const chipColor = under ? "#6ee7b7" : "#fbbf24";
                const bg = under ? "rgba(16,185,129,0.14)" : "rgba(245,158,11,0.14)";
                const brd = under ? "rgba(16,185,129,0.65)" : "rgba(245,158,11,0.65)";
                return (
                  <span
                    data-testid="vault-auto-fleet-budget-delta"
                    className="ml-1 px-2 py-0.5 rounded-full text-[9px] tabular-nums"
                    style={{ background: bg, border: `1px solid ${brd}`, color: chipColor,
                             boxShadow: `0 0 8px ${brd}` }}
                  >
                    {under ? "−" : "+"}${Math.abs(delta).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} {under ? "UNDER" : "OVER"} BUDGET
                    <span className="text-slate-500 ml-1">· ${budget.toLocaleString()}</span>
                  </span>
                );
              })()}
            </div>
            {dupCount > 0 && (
              <div
                className="mt-2 flex items-center justify-between gap-2 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md"
                data-testid="vault-auto-dup-banner"
                style={{
                  background: "rgba(245,158,11,0.10)",
                  border: "1px solid rgba(245,158,11,0.55)",
                  color: "#fbbf24",
                  boxShadow: "0 0 12px #F59E0B",
                }}
              >
                <span>⚠ Duplicate-guard · {dupCount} row{dupCount === 1 ? "" : "s"} flagged</span>
                {selectedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={bulkPurge}
                    disabled={bulkPurging}
                    data-testid="vault-auto-bulk-purge-btn"
                    className="px-2 py-0.5 rounded-md tracking-widest disabled:opacity-40"
                    style={{
                      background: "rgba(245,158,11,0.22)",
                      border: "1px solid rgba(245,158,11,0.75)",
                      color: "#fbbf24",
                      boxShadow: "0 0 10px rgba(245,158,11,0.45)",
                    }}
                  >
                    {bulkPurging ? "Purging…" : `⚡ Bulk Purge ${selectedIds.size}`}
                  </button>
                )}
              </div>
            )}
            {filtered.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="vault-auto-receipt-list">
                {dupIdsInSlice.length > 0 && (
                  <label
                    className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-widest text-slate-400 px-2.5 py-1 select-none cursor-pointer"
                    data-testid="vault-auto-master-select-label"
                  >
                    <input
                      type="checkbox"
                      checked={allDupSelected}
                      onChange={toggleMasterSelected}
                      data-testid="vault-auto-master-select"
                      className="accent-amber-400 w-3 h-3"
                    />
                    Select all duplicates ({dupIdsInSlice.length})
                  </label>
                )}
                {filtered.slice(0, 40).map((r, i) => {
                  const flagged = isDup(r);
                  const isSelected = r.document_id ? selectedIds.has(r.document_id) : false;
                  return (
                    <div
                      key={r.document_id || `${dupSig(r)}-${i}`}
                      data-testid={`vault-auto-receipt-row-${i}`}
                      data-duplicate={flagged ? "true" : "false"}
                      className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-[10px] font-mono transition-shadow"
                      style={{
                        background: "rgba(255,255,255,0.02)",
                        border: `1px solid ${flagged ? "rgba(245,158,11,0.65)" : "rgba(148,163,184,0.12)"}`,
                        boxShadow: flagged ? "0 0 12px #F59E0B" : undefined,
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        color: "#cbd5e1",
                      }}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {flagged && r.document_id && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelected(r.document_id)}
                            data-testid={`vault-auto-row-select-${i}`}
                            className="accent-amber-400 w-3 h-3 shrink-0"
                            aria-label={`Select ${r.merchant || "row"} for bulk purge`}
                          />
                        )}
                        <div className="truncate">
                          <span className="uppercase tracking-widest" style={{ color: flagged ? "#fbbf24" : cfg.color }}>
                            {r.category || "auto"}
                          </span>
                          <span className="mx-1.5 text-slate-500">·</span>
                          <span className="text-slate-200">{r.merchant || "—"}</span>
                          <span className="mx-1.5 text-slate-500">·</span>
                          <span className="text-slate-400">{r.date || "—"}</span>
                          {flagged && (
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded-full text-[8px] tracking-widest"
                              style={{ background: "rgba(245,158,11,0.18)", border: "1px solid rgba(245,158,11,0.65)", color: "#fbbf24" }}
                              data-testid={`vault-auto-dup-chip-${i}`}
                            >
                              DUP
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="tabular-nums" style={{ color: flagged ? "#fbbf24" : "#e2e8f0" }}>
                          {fmt(r.total)}
                        </span>
                        {flagged && (
                          <button
                            type="button"
                            onClick={() => openDiff(r)}
                            disabled={purging === r.document_id}
                            data-testid={`vault-auto-merge-purge-${i}`}
                            className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md transition-colors disabled:opacity-40"
                            style={{
                              background: "rgba(245,158,11,0.14)",
                              border: "1px solid rgba(245,158,11,0.65)",
                              color: "#fbbf24",
                              backdropFilter: "blur(12px)",
                              WebkitBackdropFilter: "blur(12px)",
                              boxShadow: "0 0 10px rgba(245,158,11,0.35)",
                            }}
                          >
                            {purging === r.document_id ? "Purging…" : "⚡ Merge & Purge"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filtered.length > 40 && (
                  <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 pt-1">
                    · showing 40 of {filtered.length}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
      {doc && (
        <div className="mt-3 rounded-lg p-3 border" style={{ borderColor: cfg.color + "60", background: "rgba(15,23,42,0.55)" }} data-testid={`vault-${kind}-result`}>
          <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
            {kind === "auto" && (
              <>
                <Cell k="Merchant"  v={doc.merchant || "—"} />
                <Cell k="Category"  v={doc.category || "—"} />
                <Cell k="Date"      v={doc.date || "—"} />
                <Cell k="Repair Total"     v={fmt(doc.total)} />
                <Cell
                  k={`Business Use % · ${mileageMeta.business_miles.toLocaleString()} / ${mileageMeta.total_miles.toLocaleString()} mi`}
                  v={`${Math.round(businessUsePct * 100)}%`}
                />
                <Cell
                  k="Calculated Business Deduction · IRC §162"
                  v={fmt(Number(doc.total || 0) * businessUsePct)}
                  highlight="#10B981"
                  testid={`vault-auto-calc-deduction-${doc.document_id || "row"}`}
                />
              </>
            )}
            {kind === "home" && (
              <div className="col-span-2 sm:col-span-3 pl-3" data-testid="vault-home-doc-row">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Cell k="Provider"  v={doc.provider || "—"} />
                  <Cell k="Utility"   v={doc.utility_type || "—"} />
                  <Cell k="Bill total"       v={fmt(doc.total)} />
                  {user?.home_office_pct > 0 ? (
                    <>
                      <Cell
                        k={`Workspace Sq-Ft Ratio · IRC §280A(c)(1)`}
                        v={`${Math.round((doc.home_office_pct || user?.home_office_pct || 0) * 100)}%`}
                      />
                      <Cell
                        k={`Deductible Balance @ ${Math.round((doc.home_office_pct || user?.home_office_pct || 0) * 100)}%`}
                        v={fmt(doc.deductible_amount || Number(doc.total || 0) * (user?.home_office_pct || 0))}
                        highlight="#06B6D4"
                        testid={`vault-home-deductible-${doc.document_id || "row"}`}
                      />
                      <Cell
                        k="Simplified Method Ceiling · $5 × 300 sq ft"
                        v={fmt(1_500)}
                      />
                    </>
                  ) : null}
                </div>
              </div>
            )}
            {kind === "capital" && (
              <>
                <Cell k="Asset"     v={doc.asset_name || "—"} />
                <Cell k="Class"     v={doc.asset_class || "—"} />
                <Cell k="Serial"    v={doc.serial_number || "—"} />
                <Cell k="Price"     v={fmt(doc.purchase_price)} />
                <Cell
                  k={`Year-1 MACRS · IRC §168`}
                  v={fmt(doc.depreciation_schedule?.[0]?.deduction)}
                />
                <Cell
                  k="5-Year Total Recoverable"
                  v={fmt((doc.depreciation_schedule || []).reduce((s, r) => s + Number(r.deduction || 0), 0))}
                  highlight="#FFD700"
                  testid={`vault-capital-5yr-total-${doc.document_id || "row"}`}
                />
              </>
            )}
          </div>
          {kind === "capital" && doc.depreciation_schedule && (
            <DepreciationCurve
              schedule={doc.depreciation_schedule}
              color={cfg.color}
              elected={!!section179Elected[doc.document_id]}
              bonusElected={!!section168kElected[doc.document_id]}
              purchasePrice={doc.purchase_price}
              aggregate179={aggregate179}
              cap179={CAP_179}
              onToggleElection={(next) => toggleSection179(doc.document_id, doc.purchase_price, next)}
              onToggleBonus={(next) => toggleSection168k(doc.document_id, next)}
            />
          )}
          <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-1">
            <FileText size={10} /> {doc.compliance_verification_token?.code || "—"} · {doc.compliance_verification_token?.title || ""}
          </div>
        </div>
      )}
      {kind === "auto" && (
        <DiffModal
          target={pendingDiff?.target}
          sibling={pendingDiff?.sibling}
          color={cfg.color}
          busy={!!purging}
          onCancel={() => setPendingDiff(null)}
          onConfirm={(purgeTargetId) => {
            if (!purgeTargetId) return;
            const row =
              pendingDiff?.target?.document_id === purgeTargetId
                ? pendingDiff.target
                : pendingDiff?.sibling?.document_id === purgeTargetId
                  ? pendingDiff.sibling
                  : null;
            if (!row?.document_id) return;
            (async () => {
              setPurging(row.document_id);
              const toastId = toast.loading(`Merging & purging ${row.merchant || "row"}…`);
              try {
                await api.delete(`/v1/ingest/vault/auto/${row.document_id}`);
                await refetchAuto();
                toast.dismiss(toastId);
                // Single-row purge also gets a 5-second Undo affordance.
                toast(`⚡ Purged · ${row.merchant || row.document_id}`, {
                  duration: 5000,
                  action: {
                    label: "✍️ Undo Purge",
                    onClick: async () => {
                      const undoId = toast.loading(`Restoring ${row.merchant || "row"}…`);
                      try {
                        await api.post("/v1/ingest/vault/auto/restore", { rows: [row] });
                        await refetchAuto();
                        toast.success(`Restored · ${row.merchant || row.document_id}`, { id: undoId });
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Undo failed", { id: undoId });
                      }
                    },
                  },
                });
                setPendingDiff(null);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Purge failed", { id: toastId });
              } finally {
                setPurging(null);
              }
            })();
          }}
        />
      )}
      <CsvColumnChooser
        open={!!csvGate}
        columns={csvGate?.columns || []}
        color={csvGate?.color || cfg.color}
        onCancel={() => setCsvGate(null)}
        onConfirm={(picked) => {
          try { csvGate?.build?.(picked); } catch { /* build errors self-toast */ }
          setCsvGate(null);
        }}
      />
      <style>{`
        @keyframes vault-halo-breathe {
          0%, 100% { box-shadow: 0 0 0 1px var(--vault-color), 0 0 16px var(--vault-color)55, 0 0 24px var(--vault-color)33; }
          50%      { box-shadow: 0 0 0 2px var(--vault-color), 0 0 32px var(--vault-color)aa, 0 0 48px var(--vault-color)77; }
        }
        .vault-halo { animation: vault-halo-breathe 4s ease-in-out infinite; }
        @keyframes vault-laser-sweep {
          0% { top: 0%; opacity: 0.9; }
          100% { top: 100%; opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .vault-halo { animation: none !important; } }
      `}</style>
      <ConfirmResetDialog
        open={resetOpen}
        title={`Clear ${cfg.label} · ${taxYear} Slice?`}
        body={`⚠️ Are you sure you want to permanently clear this data ledger? This will delete every ${cfg.label.toLowerCase()} row filed under the ${taxYear} tax year. Rows from other years on the Multi-Year Slice slider will remain intact. This action cannot be undone.`}
        confirmLabel={`♻️ Clear ${taxYear} Slice`}
        busy={resetBusy}
        onCancel={() => setResetOpen(false)}
        onConfirm={performVaultReset}
        testid={`vault-${kind}-reset`}
      />
    </div>
  );
}

function Cell({ k, v, highlight, testid }) {
  return (
    <div
      className="rounded p-2 border bg-slate-950/50"
      style={{
        borderColor: highlight ? `${highlight}80` : "rgba(255,255,255,0.05)",
        boxShadow: highlight ? `inset 0 0 0 1px ${highlight}30, 0 0 12px ${highlight}25` : undefined,
      }}
      data-testid={testid}
    >
      <div
        className="text-[9px] uppercase tracking-widest"
        style={{ color: highlight || "#64748b" }}
      >
        {k}
      </div>
      <div
        className="font-bold truncate"
        style={{ color: highlight || "#ffffff", textShadow: highlight ? `0 0 8px ${highlight}80` : undefined }}
      >
        {v}
      </div>
    </div>
  );
}

export default SpecializedVault;
